import "./index.scss";

import { Box, Grid, Modal, Stack } from "@mui/material";
import { ReactComponent as IconMove } from "assets/images/icons/moving-horizontal.svg";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import { ReactComponent as IconCopy } from "assets/images/square-buttons/duplicate.svg";
import { ReactComponent as IconTrash } from "assets/images/square-buttons/trash.svg";
import Button from "components/Button";
import ConfirmationBox from "components/ConfirmationBox";
import Input from "components/Input";
import ScreenLoader from "components/ScreenLoader";
import { MSG_OPERATION_FAILED, MSG_OPERATION_SUCCESSFUL } from "constants/index";
import FormWrapper from "containers/Members/components/FormWrapper";
import _ from "lodash";
import { Protocol, ProtocolSet } from "models/protocol";
import {
  addProtocolEntry,
  getProtocolById,
  updateProtocolEntry,
} from "modules/protocol/protocolServices";
import { memo, useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ReactSortable } from "react-sortablejs";
import { toast } from "react-toastify";

const ProtocolCreateEditModal = (props: { open: boolean; onClose: any; entryId?: number }) => {
  const { open, onClose, entryId } = props;
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [protocolSets, setProtocolSets] = useState<ProtocolSet[]>([]);
  const [showDataWillBeLostModal, setShowDataWillBeLostModal] = useState(false);

  const methods = useForm<Protocol>({
    defaultValues: {
      description: "-",
      id: 0,
      min_drop_time: 0,
      min_lift_time: 0,
      rest_time: 0,
      sets: [],
      title: "",
    },
  });
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    control,
    reset,
  } = methods;

  useEffect(() => {
    if (entryId && open) {
      setLoading(true);
      getProtocolById({ id: entryId })
        .then((r) => {
          if (r.success) {
            const fetchedProtocol = r.data;
            setValue("id", fetchedProtocol.id);
            setValue("title", fetchedProtocol.title);
            setValue("description", fetchedProtocol.description);
            setValue("min_lift_time", fetchedProtocol.min_lift_time);
            setValue("min_lift_time_for_display", fetchedProtocol.min_lift_time_for_display);
            setValue("min_drop_time", fetchedProtocol.min_drop_time);
            setValue("min_drop_time_for_display", fetchedProtocol.min_drop_time_for_display);
            setValue("rest_time", fetchedProtocol.rest_time);
            setValue("sets", fetchedProtocol.sets);
            setProtocolSets(fetchedProtocol.sets);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [entryId, open]);

  useEffect(() => {
    setValue("sets", protocolSets);
  }, [protocolSets]);

  //#region event handlers
  const handleCloseModal = useCallback((isRefreshNeeded = false) => {
    if (isRefreshNeeded === true) onClose(isRefreshNeeded);
    else setShowDataWillBeLostModal(true);
  }, []);

  const onCloseConfirm = (discardChanges = false) => {
    if (discardChanges) {
      reset({
        description: "-",
        id: 0,
        min_drop_time: 0,
        min_drop_time_for_display: 0,
        min_lift_time: 0,
        min_lift_time_for_display: 0,
        rest_time: 0,
        sets: [],
        title: "",
      });
      setProtocolSets([]);
      setShowDataWillBeLostModal(false);
      onClose();
    } else {
      setShowDataWillBeLostModal(false);
    }
  };

  const handleConfirm = (data) => {
    setIsSaving(true);
    const createOrUpdateAPI = entryId ? updateProtocolEntry : addProtocolEntry;
    createOrUpdateAPI(data)
      .then((r) => {
        if (r.success) {
          toast(MSG_OPERATION_SUCCESSFUL, { type: "success" });
          handleCloseModal(true);
        } else {
          throw r.message;
        }
      })
      .catch((e) => {
        console.log(e);
        toast(MSG_OPERATION_FAILED, { type: "error" });
      })
      .finally(() => setIsSaving(false));
  };

  const handleAppendToSet = (e) => {
    e.preventDefault();
    setProtocolSets([...protocolSets, { id: 0, max_reps: 0, min_reps: 0 }]);
  };

  const handleSetFieldValueUpdate = (indexOfEntry, keyToUpdate, value) => {
    let setsCopy = [...protocolSets];
    setsCopy[indexOfEntry][keyToUpdate] = value;
    setProtocolSets(setsCopy);
  };

  const handleCopyInSet = (indexOfEntryToCopy) => {
    let setsCopy = [...protocolSets];
    setsCopy.splice(indexOfEntryToCopy + 1, 0, {
      id: 0,
      max_reps: setsCopy[indexOfEntryToCopy].max_reps,
      min_reps: setsCopy[indexOfEntryToCopy].min_reps,
    });
    setProtocolSets(setsCopy);
  };

  const handleDeleteInSet = (indexOfEntry) => {
    let setsCopy = [...protocolSets];
    setsCopy.splice(indexOfEntry, 1);
    setProtocolSets(setsCopy);
  };
  //#endregion event handlers

  return (
    <>
      <Modal
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
        open={open}
        onClose={handleCloseModal}
      >
        <Stack className="personal-details-modal-container protocol-create-edit-modal">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom="40px"
            className="header"
          >
            <h1>{entryId ? `Edit` : `Create`} Protocol</h1>
            <DeleteIcon width="24px" height="24px" onClick={() => handleCloseModal()} />
          </Stack>
          {loading ? (
            <ScreenLoader isScreenLoader />
          ) : (
            <form onSubmit={handleSubmit(handleConfirm)} className="manage-protocol-container">
              <Box>
                <FormWrapper label="Protocol Name" isRequired>
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Input
                          {...field}
                          hasError={!_.isEmpty(errors.title)}
                          placeholder="Please enter"
                          maxLength={25}
                        />
                        {errors.title?.type === "required" && (
                          <h4 className="error">This field is required</h4>
                        )}
                      </Stack>
                    )}
                  />
                </FormWrapper>
              </Box>
              {/* <Box>
              <FormWrapper label="Description" isRequired>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Stack spacing={1}>
                      <Input
                        {...field}
                        hasError={!_.isEmpty(errors.description)}
                        placeholder="Please enter"
                        maxLength={25}
                      />
                      {errors.description?.type === "required" && (
                        <h4 className="error">This field is required</h4>
                      )}
                    </Stack>
                  )}
                />
              </FormWrapper>
            </Box> */}
              <Box>
                <h2 className="headerSpacing">Times</h2>
                <Grid container>
                  <Grid item md={12} lg={4} width={"100%"}>
                    <FormWrapper label="Min Lift Time" isRequired>
                      <Controller
                        name="min_lift_time_for_display"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1}>
                            <Input
                              value={value}
                              onChange={(e) => {
                                onChange(+e.target.value);
                                setValue("min_lift_time", +e.target.value * 100);
                              }}
                              type="number"
                              step="any"
                              min={0}
                              max={10}
                              hasError={!_.isEmpty(errors.min_lift_time_for_display)}
                            />
                            {errors.min_lift_time_for_display?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </FormWrapper>
                  </Grid>
                  <Grid item md={12} lg={4} width={"100%"}>
                    <FormWrapper label="Min Drop Time" isRequired>
                      <Controller
                        name="min_drop_time_for_display"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1}>
                            <Input
                              value={value}
                              onChange={(e) => {
                                onChange(+e.target.value);
                                setValue("min_drop_time", +e.target.value * 100);
                              }}
                              type="number"
                              step="any"
                              min={0}
                              max={10}
                              hasError={!_.isEmpty(errors.min_drop_time_for_display)}
                            />
                            {errors.min_drop_time_for_display?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </FormWrapper>
                  </Grid>
                  <Grid item md={12} lg={4} width={"100%"}>
                    <FormWrapper label="Rest Time" isRequired>
                      <Controller
                        name="rest_time"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Stack spacing={1}>
                            <Input
                              {...field}
                              type="number"
                              hasError={!_.isEmpty(errors.rest_time)}
                              min={0}
                              max={300}
                            />
                            {errors.rest_time?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </FormWrapper>
                  </Grid>
                </Grid>
              </Box>
              <Box className="details-container">
                <Box mt={"10px"} className="details-box">
                  <Stack
                    direction={"row"}
                    alignItems="center"
                    justifyContent={"space-between"}
                    mb="10px"
                  >
                    <p className="title">Sets</p>
                    <Button
                      label="Add Set"
                      className="confirm-button"
                      onClick={(e) => handleAppendToSet(e)}
                      type="button"
                    />
                  </Stack>
                  <Box className="set-items-container">
                    <ReactSortable
                      list={protocolSets}
                      setList={setProtocolSets}
                      animation={200}
                      direction="horizontal"
                      handle=".move"
                    >
                      {protocolSets.map((field, index) => (
                        <Box className="set-item" key={`set${index}`}>
                          <Stack direction={"row"} alignItems="center" gap="6px">
                            <IconMove className="move" />
                            <Box flexGrow={1}>
                              <p className="title">Set {index + 1}</p>
                            </Box>
                            <IconCopy onClick={() => handleCopyInSet(index)} />
                            <IconTrash onClick={() => handleDeleteInSet(index)} />
                          </Stack>
                          <Stack className="info">
                            <Stack className="info-header">
                              <p>Min Reps</p>
                              <p>Max Reps</p>
                            </Stack>
                            <Stack className="info-row">
                              <p>
                                <input
                                  value={field.min_reps}
                                  type="number"
                                  min="0"
                                  max="100"
                                  onChange={(e) =>
                                    handleSetFieldValueUpdate(index, "min_reps", e.target.value)
                                  }
                                />
                              </p>
                              <p>
                                <input
                                  value={field.max_reps}
                                  type="number"
                                  min="0"
                                  max="100"
                                  onChange={(e) =>
                                    handleSetFieldValueUpdate(index, "max_reps", e.target.value)
                                  }
                                />
                              </p>
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </ReactSortable>
                  </Box>
                </Box>
              </Box>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop="30px">
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Button
                    label="Cancel"
                    className="text-button"
                    onClick={() => handleCloseModal()}
                    type="button"
                  />
                  <Button label="Save" className="confirm-button" loading={isSaving} />
                </Stack>
              </Stack>
            </form>
          )}
        </Stack>
      </Modal>
      <ConfirmationBox
        open={showDataWillBeLostModal}
        onClose={onCloseConfirm}
        title={"Discard Changes?"}
        message={<>If answered Yes, all information filled will be lost.</>}
        type={"confirmation"}
      />
    </>
  );
};

export default memo(ProtocolCreateEditModal);
